import { useCallback } from 'react';
import { FilterSpecification, Layer, LayerProps } from 'shared/map-exports';
import { usePoisState } from 'state/usePoisState';
import { FirePerimeterPoi } from 'shared/types';
import { MapLayerProps } from 'features/mapping/mapping.types';
import VisibilityToggledSource from './VisibilityToggledSource';
import { getSelectedPatternStyle, getSelectedStrokeStyle } from '../utils';
import { MapboxFeature, useMapLayerEvents } from './useMapLayerEvents';
import { BEFORE_LAYER_ID } from '../styles/constants';
import { getWatchdutyTileUrl } from './getWatchdutyTileUrl';

type ActiveFirePerimetersProperties = {
  source: string;
  source_date_current: string;
  source_display_string: string;
};

const FIRIS_LAYER_REFRESH_SECONDS = 60;
const FIRIS_LAYER_OUTER_STROKE_WIDTH = 8;

const PERIMETERS_FILL_LAYER_ID = 'perimeters-fill';

// Filters used for applying different styles to different fire perimeter sources.
const firisLayerFilter: FilterSpecification = ['!=', 'source', 'nifc'];
const nifcLayerFilter: FilterSpecification = ['==', 'source', 'nifc'];

const activeFirePerimetersFillStyle: LayerProps = {
  type: 'fill',
  'source-layer': 'fireperimeters_active',
  paint: {
    'fill-color': '#FF0000',
    'fill-opacity': ['case', ['==', ['get', 'source'], 'nifc'], 0.25, 0.4],
  },
};

const nifcPerimetersStrokeStyle: LayerProps = {
  type: 'line',
  'source-layer': 'fireperimeters_active',
  filter: nifcLayerFilter,
  paint: {
    'line-color': '#FF0000',
    'line-opacity': 0.75,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 18, 3.5],
  },
};

const firisPerimetersStrokeStyle: LayerProps = {
  type: 'line',
  'source-layer': 'fireperimeters_active',
  filter: firisLayerFilter,
  paint: {
    'line-color': '#FD2D2C',
    'line-opacity': 1,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 18, 2.5],
    'line-dasharray': [2, 1],
  },
};

const firisPerimetersBorderStyle: LayerProps = {
  type: 'line',
  'source-layer': 'fireperimeters_active',
  filter: firisLayerFilter,
  layout: {
    'line-cap': 'square',
  },
  paint: {
    'line-color': '#FD2D2C',
    'line-opacity': 0.32,
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      12,
      FIRIS_LAYER_OUTER_STROKE_WIDTH / 12,
      18,
      FIRIS_LAYER_OUTER_STROKE_WIDTH,
    ],
    'line-offset': [
      'interpolate',
      ['linear'],
      ['zoom'],
      12,
      -FIRIS_LAYER_OUTER_STROKE_WIDTH / 24,
      18,
      -FIRIS_LAYER_OUTER_STROKE_WIDTH / 2,
    ],
  },
};

const ActiveFirePerimetersLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const { selectedPoi, setSelectedPoi } = usePoisState();

  const handleClickCommon = useCallback(
    (poi: FirePerimeterPoi) => {
      setSelectedPoi({
        type: 'firePerimeter',
        id: poi.id,
        metadata: poi,
      });
    },
    [setSelectedPoi],
  );

  const handleClick = useCallback(
    (
      geoJsonFeatures: MapboxFeature<ActiveFirePerimetersProperties>[],
    ): void => {
      for (const feature of geoJsonFeatures) {
        const { properties } = feature;
        const activeFirePerimeter: FirePerimeterPoi = {
          id: feature.id,
          source: properties.source_display_string,
          dateCurrent: properties.source_date_current,
        };
        handleClickCommon(activeFirePerimeter);
      }
    },
    [handleClickCommon],
  );

  useMapLayerEvents<ActiveFirePerimetersProperties>({
    layerId: PERIMETERS_FILL_LAYER_ID,
    onClick: handleClick,
  });

  const selectedId =
    (selectedPoi?.type === 'firePerimeter' && selectedPoi.id) || '';

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': 'fireperimeters_active',
    },
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': 'fireperimeters_active',
    },
  });

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl('fireperimeters_active')}
      volatile
      visible={visible}
      refreshIntervalSeconds={FIRIS_LAYER_REFRESH_SECONDS}
    >
      <Layer
        id={PERIMETERS_FILL_LAYER_ID}
        beforeId={BEFORE_LAYER_ID}
        {...activeFirePerimetersFillStyle}
      />
      <Layer
        id="nifc-perimeter-stroke"
        beforeId={BEFORE_LAYER_ID}
        {...nifcPerimetersStrokeStyle}
      />
      <Layer
        id="firis-perimeter-stroke"
        beforeId={BEFORE_LAYER_ID}
        {...firisPerimetersStrokeStyle}
      />
      <Layer
        id="firis-perimeter-border"
        beforeId={BEFORE_LAYER_ID}
        {...firisPerimetersBorderStyle}
      />
      <Layer id="perimeter-fill-selected" {...selectedFillStyle} />
      <Layer id="perimeter-stroke-selected" {...selectedStrokeStyle} />
    </VisibilityToggledSource>
  );
};

export default ActiveFirePerimetersLayer;
